import $ from 'jquery'
import videojs from 'video.js'
import 'videojs-hotkeys'
import 'videojs-contrib-quality-levels'
import 'videojs-http-source-selector'

import registerThumbnailPlugin from './thumbnails'
import { throttle } from 'throttle-debounce'
import debounce from '../../helpers/debounce'
import { sizeWrapper } from './wrapper'

/* Replace require with import per https://www.reddit.com/r/sveltejs/comments/wxkxzz/is_there_a_straightforward_way_for_replacing/ */
import videojsChromecast from '@silvermine/videojs-chromecast'
videojsChromecast(videojs)
import videojsAirplay from '@silvermine/videojs-airplay'
videojsAirplay(videojs)

import './seek-buttons'

import 'video.js/dist/video-js.css'
import './index.scss'
import './custom_plugins/simpler_subtitle_button'
import './custom_plugins/enhanced_audio_button'
import './custom_plugins/playback_rate_button'
import {
  applyModernStyleToPlayerIfApplicable,
  useModernStyle,
} from './modern/index.js'
import ChromecastSessionManager from '@silvermine/videojs-chromecast/src/js/chromecast/ChromecastSessionManager'
import { users } from '~/routes'
import { inViewport } from '~/common/helpers/in_viewport'
import { popupOnPlayIfDesired } from './popup'
import { setupAutoPlay } from './autoplay'
import { setupImageOverlay } from './image_overlay'
import { setupWelcomeBackOverlay } from './welcome-back-overlay'
import { setupVideoControlsOverlay } from './video-controls-overlay'
import { decodeChromecastSource } from './chromecast'
import { restartWhenStalled } from './restart-when-stalled'

registerThumbnailPlugin(videojs)

// Shared function to setup play tracking for both video and audio
const setupPlayTracking = function (player, stats_url, stats_data) {
  if (!stats_url || !stats_data) return

  let video_play_id = null
  let session_start = null
  let last_position = null

  player.on('timeupdate', function () {
    if (!player.paused()) {
      last_position = player.currentTime()
      session_start = session_start || last_position
    }
  })

  const reportSession = function () {
    if (session_start && last_position) {
      let data = $.extend(
        { start_sec: session_start, end_sec: last_position },
        stats_data
      )

      // update video play if needed
      if (video_play_id) {
        data = $.extend({ video_play_id: video_play_id }, data)
      }

      if (data.end_sec - data.start_sec > 5) {
        // stats_url = /videos/:asset_id/track_view (same endpoint for both audio and video)
        $.ajax({
          url: stats_url,
          type: 'POST',
          data,
          success: (data) => {
            if (data && data.video_play_id) {
              video_play_id = data.video_play_id
            }
          },
          global: false,
        })
      }
    }
  }

  const handleSeek = () => {
    video_play_id = null
    session_start = player.currentTime()
  }

  player.on('seeked', debounce(handleSeek))

  player.on('play', () => {
    if (!session_start) {
      session_start = player.currentTime()
    }
  })

  player.on('pause', () => {
    if (!player.seeking()) {
      reportSession()
    }
  })

  player.on('dispose', reportSession)
}

const enforceVideoMaxHeight = function ($elem, max_height) {
  if (
    $elem.closest('.no-videojs-max-height').length == 0 &&
    $elem.outerHeight() !== max_height
  ) {
    const width = $elem.outerWidth()
    const org = 1 / $elem.data('ratio')
    const fixed = max_height / width
    if (fixed < org) {
      $elem.css('padding-top', `${fixed * 100}%`)
    } else {
      $elem.css('padding-top', '')
    }
  }
}

const getAssetId = (elem) => {
  return $(elem).data('asset-id')
}

const getPlaybackPosition = (elem) => {
  // Playback position might be stored server-side for certain assets (e.g main assets on course)
  //  --> So the position can be remembered across mobile app + web browser for a logged in user.
  // Prefer using position from server if it's available, otherwise fallback to local storage.
  const assetId = getAssetId(elem)

  if (!assetId) return

  if ($(elem).data('start-from-beginning') || $(elem).data('autoplay')) return

  return (
    getPlaybackPositionFromUrl(assetId) ||
    getPlaybackPositionFromLocalStorage(elem, assetId)
  )
}

const getPlaybackPositionFromUrl = (assetId) => {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const params = Object.fromEntries(urlSearchParams.entries())

  if (params['media_position_' + assetId]) {
    return params['media_position_' + assetId]
  }
}

const getPlaybackPositionFromLocalStorage = (elem, assetId) => {
  const positionData = elem.data('position')
  const restoreTimeKey = `simplero:asset_${assetId}:time`
  let restoreTime =
    positionData?.position_sec || localStorage.getItem(restoreTimeKey) || 0

  if (restoreTime < 10) {
    // dont restore if less than 10 seconds
    restoreTime = null
  } else {
    // set it to five seconds back, just so you get a chance to catch up
    restoreTime = restoreTime - 5
  }

  return restoreTime
}

const handlePlaybackPositionRestore = function (
  player,
  elem,
  playbackPosition
) {
  const asset_id = getAssetId(elem)

  if (!asset_id) return

  const positionData = $(elem).data('position')
  const restoreTimeKey = `simplero:asset_${asset_id}:time`
  let restoreTime = playbackPosition

  player.one('canplay', function (e) {
    // Do not restore if less than 10 seconds until video ends
    if (restoreTime && player.duration() - restoreTime > 10) {
      player.currentTime(restoreTime)
    }

    // Make sure we don't do this again
    restoreTime = null
  })

  player.on('timeupdate', function (e) {
    if (player.paused()) {
      return
    }

    const position = player.currentTime()

    // Save on server
    if (positionData) {
      updatePlaybackPosition(
        position,
        asset_id,
        positionData.context_type,
        positionData.context_id
      )
    }

    // Save on local storage
    if (position > player.duration() - 20) {
      localStorage.removeItem(restoreTimeKey)
    } else {
      localStorage.setItem(restoreTimeKey, position)
    }
  })
}

const updatePlaybackPosition = throttle(
  10000,
  (position, asset_id, context_type, context_id) => {
    $.ajax({
      url: users.updatePlaybackPosition.path(),
      type: 'POST',
      data: {
        asset_id,
        position,
        context_type,
        context_id,
      },
      global: false,
    })
  },
  {
    noLeading: true,
  }
)

const updatePreferredPlaybackRate = debounce((newRate) => {
  $.ajax({
    url: users.updatePreferredPlaybackRate.path(),
    type: 'POST',
    data: { preferred_playback_rate: newRate },
    global: false,
  })
})

const handlePlaybackRateChange = function (player, elem) {
  console.log('handlePlaybackRateChange', elem.id)
  let preferredRate = parseFloat($(elem).data('preferred-playback-rate'))
  console.log(
    'handlePlaybackRateChange',
    elem.id,
    'preffered_playback_rate',
    preferredRate
  )

  // all users by default have preferredRate as 1.0,
  // if preferredRate is undefined or false - it means that preferredRate disabled
  // play video/audio with initial speed and not save preferredRate for user
  if (!preferredRate) {
    return
  }

  // We need to get the closest supported rate because the value might be also updated on the mobile map
  // which might in the future support more/different playback rates
  let pauseRateChangeListener = false
  let supportedRates = player.playbackRates()
  let closestSupportedRate = 1.0

  for (const rate of supportedRates) {
    if (
      Math.abs(preferredRate - rate) <
      Math.abs(preferredRate - closestSupportedRate)
    ) {
      closestSupportedRate = rate
    }
  }

  const changePlaybackRateWithoutEvent = (newRate) => {
    pauseRateChangeListener = true
    console.log(
      'handlePlaybackRateChange',
      elem.id,
      'changing playback rate to',
      newRate
    )
    player.playbackRate(newRate)
    setTimeout(() => (pauseRateChangeListener = false), 100)
  }

  console.log('handlePlaybackRateChange', elem.id, {
    closestSupportedRate,
    pauseRateChangeListener,
  })

  player.one('canplay', () => {
    console.log('handlePlaybackRateChange', elem.id, 'canplay')

    if (closestSupportedRate) {
      console.log('handlePlaybackRateChange', elem.id, 'canplay if success')
      changePlaybackRateWithoutEvent(closestSupportedRate)
    }
  })

  $(document).on('simplero:playback-rate-change', (e, newRate) => {
    console.log(
      'handlePlaybackRateChange',
      elem.id,
      'on simplero:playback-rate-change'
    )
    closestSupportedRate = null // set this to null so canplay event above does not update the playback rate to old value when clicking play
    changePlaybackRateWithoutEvent(newRate)
  })

  player.on('ratechange', () => {
    console.log(
      'handlePlaybackRateChange',
      elem.id,
      'rate change',
      pauseRateChangeListener
    )
    if (pauseRateChangeListener) {
      return
    }
    const newRate = player.playbackRate()
    console.log(
      'handlePlaybackRateChange',
      elem.id,
      'ratechange new rate',
      newRate
    )
    updatePreferredPlaybackRate(newRate)
    $(document).trigger('simplero:playback-rate-change', newRate)
  })
}

const enhanceBackgroundPlaybackChancesOnMobileApp = function (player) {
  if (!window.Bridge) return

  const listener = () => {
    if (player.ignoreOneVisibilityChangeEvent) {
      player.ignoreOneVisibilityChangeEvent = false
      if (player.clearIgnoreOneVisibilityChangeEventTimeout)
        clearTimeout(player.clearIgnoreOneVisibilityChangeEventTimeout)
      return
    }

    document.hidden ? player.play() : null
  }

  const addListener = () =>
    document.addEventListener('visibilitychange', listener)
  const removeListener = () =>
    document.removeEventListener('visibilitychange', listener)

  let pauseTimeout = null

  player.on('play', () => {
    if (pauseTimeout) clearTimeout(pauseTimeout)
    removeListener()
    addListener()
  })

  player.on('pause', () => {
    pauseTimeout = setTimeout(() => {
      removeListener()
    }, 700)
  })
}

export const initializeVideoPlayer = function (elem) {
  const $elem = $(elem)

  // Bail on setup if the video is already initialzied.
  if ($elem.parents('.video-js').length) {
    return
  }

  const snapshot = $elem[0].outerHTML
  const elemClasses = $elem.attr('class')
  const max_height = $elem.data('max-height')
  const stats_url = $elem.data('stats-url')
  const stats_data = $elem.data('stats-data')
  const forThemed = $elem.data('for-themed')
  const options = $elem.data('video-options')

  if (options === undefined) {
    console.warn(`options for videojs not found. video-js disabled.`, elem)
    return
  }
  const chromecastOptions = options.plugins.chromecast

  // Instead of initializing chromecast when initializing the player, initialize it manually.
  // This allows us to insert the chromecast button in the proper place
  if (chromecastOptions) delete options.plugins.chromecast

  ensureUniquePlayerId($elem)
  const id = $elem.attr('id')

  // Try to help single-video pages start video playback without too much latency,
  // not used for multi-video because it could use too much bandwidth.
  $elem.attr('preload', $('video, audio').length > 1 ? 'none' : '')

  var $wrapper = $elem.parent('.video-wrapper')

  if (forThemed) {
    $elem.addClass('video-js vjs-fluid')

    // Wrap the video in a sizing wrapper to make the videos resize responsively. TODO
    if ($wrapper.length == 0) {
      $wrapper = $('<div class="video-wrapper"></div>')
      $elem.wrap($wrapper)
      sizeWrapper($elem[0])
    }

    if (elemClasses) {
      $wrapper.addClass(elemClasses).removeClass('video-js')
    }
  }

  var player = videojs(id, options, function () {
    // This is a workaround for an issue in Safari on iOS
    // $elem and elem in this closure do not reference the real videojs element in the DOM for some
    // reason. This only happens in Safari on iOS as far as we know.
    // We do a manual "reload" of the objects in-memory to get all overlays and other things
    // working.
    const element = document.getElementById(elem.id)
    const $element = $(element)
    const wrapper = element.closest('.video-wrapper')

    $element.trigger('videojs:ready')

    player = this

    // Disable fluid mode to allow sizing differently then the video's native size.
    if ($element.data('no-fluid') == '1') {
      player.fluid(false)
    }

    const playbackPosition = getPlaybackPosition($element)
    const showWelcomeBackOverlay =
      playbackPosition && $element.data('welcomeBackOverlay')

    handlePlaybackPositionRestore(player, element, playbackPosition)
    handlePlaybackRateChange(player, element)
    popupOnPlayIfDesired(player, element)

    if (showWelcomeBackOverlay) setupWelcomeBackOverlay(player, $element)
    setupVideoControlsOverlay(player, $element)

    setupPlayTracking(player, stats_url, stats_data)

    player.on('fullscreenchange', () => {
      // Focus on the main videojs element again so that keyboard shortcuts work
      player.contentEl().focus()
    })

    // Pause all other
    player.on('play', function (e) {
      document.querySelectorAll('.video-js video').forEach((video) => {
        if (video.getAttribute('id') === elem.getAttribute('id')) return
        const player = videojs(video.id)
        player.pause()
      })
    })

    restartWhenStalled(player)
    player.ready(function () {
      if (wrapper) wrapper.classList.add('player-ready')

      const nakedKey = (event) =>
        !event.ctrlKey && !event.shiftKey && !event.altKey && !event.metaKey

      // The shortcuts for autoplay and theater mode are defined on their respective components (check the stimulus actions)
      // app/components/video_js/autoplay_next_component.html.erb
      // app/components/video_js/theater_mode_toggle_component_controller.js
      this.hotkeys({
        seekStep: 10,
        enableHoverScroll: true,
        enableNumbers: false,
        playPauseKey: (event) => event.key === ' ' && nakedKey(event),
        rewindKey: (event) => event.key === 'ArrowLeft' && nakedKey(event),
        forwardKey: (event) => event.key === 'ArrowRight' && nakedKey(event),
        volumeUpKey: (event) => event.key === 'ArrowUp' && nakedKey(event),
        volumeDownKey: (event) => event.key === 'ArrowDown' && nakedKey(event),
        muteKey: (event) => event.key === 'm' && nakedKey(event),
        fullscreenKey: (event) =>
          event.key === 'f' &&
          !event.ctrlKey &&
          !event.shiftKey &&
          !event.altKey &&
          !event.metaKey,
        customKeys: {
          fasterKey: {
            key: (event) =>
              event.key === 'ArrowRight' &&
              event.altKey &&
              !event.ctrlKey &&
              !event.metaKey &&
              !event.shiftKey,
            handler: (player) =>
              player.playbackRate(
                Math.round(player.playbackRate() * 10 + 1) / 10
              ),
          },
          slowerKey: {
            key: (event) =>
              event.key === 'ArrowLeft' &&
              event.altKey &&
              !event.ctrlKey &&
              !event.metaKey &&
              !event.shiftKey,
            handler: (player) =>
              player.playbackRate(
                Math.round(Math.max(player.playbackRate() * 10 - 1, 1)) / 10
              ),
          },
        },
        skipInitialFocus: true,
        captureDocumentHotkeys: true,
        documentHotkeysFocusElementFilter(e) {
          if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) {
            return false
          }
          const playerEl = player.el()
          const $playerEl = $(playerEl)
          const $playingEl = $('.video-js.vjs-playing')

          // Consider fullscreen as playing too for our purposes
          const currentPlaying =
            $playerEl.hasClass('vjs-playing') ||
            $playerEl.hasClass('vjs-fullscreen')

          // Ignore if player is hidden
          if (!$playerEl.is(':visible')) {
            return false
          }

          if (!currentPlaying) {
            // Ignore if not in viewport
            if (!inViewport(playerEl)) {
              return false
            }

            // Ignore if there is a player that's playing but it's not this one
            if ($playingEl.length > 0) {
              return false
            }

            // Ignore if there are 2 idle players in the viewport and this one is not the first
            const $allPlayerEls = $('.video-js:visible')
            for (let el of Array.from($allPlayerEls)) {
              if (inViewport(el)) {
                if (el.id !== playerEl.id) {
                  return false
                }
                break
              }
            }
          }

          // Focus within videojs
          if ($(e).closest('.video-js').length > 0) {
            if (
              e.tagName.toLowerCase() !== 'button' &&
              !$(e).hasClass('vjs-volume-bar')
            ) {
              return true
            }
            return false
          }

          if (e.tagName.match(/^(body|html|button|a)$/i)) {
            return true
          } else {
            return false
          }
        },
      })

      // Do not setup autoplay if we're showing welcomeBackOverlay
      if (!showWelcomeBackOverlay) {
        setupAutoPlay(player, element)

        // Setup image overlay handlers for non-autoplay scenarios
        const $element = $(element)
        setupImageOverlay(player, $element)
      }

      const controlTitle = element.parentElement.dataset.controlTitles
      if (controlTitle) {
        const titles = JSON.parse(controlTitle)

        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
        if (isMac) {
          titles.playbackRateMenuButton.PlaybackRateMenuButton =
            titles.playbackRateMenuButton.PlaybackRateMenuButton.replace(
              'alt + right',
              'opt + right'
            )
        }

        const setTooltipAttributes = (el, title) => {
          el?.removeAttribute('title')
          el?.setAttribute('data-behavior', 'tooltip')
          el?.setAttribute('data-title', title)
          el?.setAttribute('data-placement', 'bottom')
          el?.setAttribute('data-offset', [30, 0])
        }

        for (const [pluginName, title] of Object.entries(titles)) {
          if (typeof title === 'string') {
            setTooltipAttributes(player.controlBar.getChild(pluginName), title)
          } else {
            const childTitle = Object.values(title)[0]
            const childName = Object.keys(title)[0]
            const child = player.controlBar
              .getChild(pluginName)
              .getChild(childName)
            setTooltipAttributes(child, childTitle)
          }
        }
      }
    })
  })

  applyModernStyleToPlayerIfApplicable(
    $elem,
    player,
    options,
    chromecastOptions
  )
  attachSimpleroMediaEvents($elem, player)

  if (chromecastOptions) {
    if (player.mobileAppChromecast) {
      player.mobileAppChromecast()
    } else {
      player.chromecast(chromecastOptions)
      // Patch currentSources when a chromecast is connected
      // Our HLS stuff doesn't play very nicely with chromecast
      // For it to even start, we have to patch videojs-chromecast to supply FMP4 as hlsVideoSegmentFormat explicitly and even then we notice that the video sometimes buffers.
      // So instead, we override currentSources when a chromecast is connected and this video is being casted to
      // return a custom source that is chrome cast compatible. This is usually a dash manifest or the raw mp4 url of the hd || general || original version if the asset doesn't have a dash version
      try {
        let chromecastSource = decodeChromecastSource(player)
        if (chromecastSource) {
          const originalCurrentSources = player.currentSources.bind(player)
          const patchedCurrentSources = () => {
            if (ChromecastSessionManager.isChromecastConnected()) {
              return [chromecastSource]
            } else {
              return originalCurrentSources()
            }
          }
          player.currentSources = patchedCurrentSources.bind(player)
        } else {
          throw new Error(
            'no chromecast source specifed, chromecast might not work because of this - to fix, ensure you pass chromecast source url when rendering the asset like AssetRenderer does.'
          )
        }
      } catch (e) {
        console.log('error while parsing chromecast source', e)
      }
    }
  }

  // Setting the default video quality to 720p or higher. The code is related to this library: https://github.com/videojs/videojs-contrib-quality-levels
  // We use videojs-http-source-selector for video quality but passing the default quality is not working on it because when it is initalized the qualities
  // are not available. Library code: https://github.com/jfujita/videojs-http-source-selector/blob/7618160677c0624d7d2893739a9f50e6af3c38fb/src/components/SourceMenuButton.js#L17-L27
  // So enabling the quality level when they are added
  player.qualityLevels().on('addqualitylevel', function (event) {
    var quality = event.qualityLevel
    if (quality.height >= 720) {
      quality.enabled = true
    } else {
      quality.enabled = false
    }
  })

  player.httpSourceSelector()
  $elem.data('videojs-player', player)
  $elem.addClass('video-js--initialized')

  const div = $('#' + id) // videojs replaces the video tag with a div
  if (max_height) {
    enforceVideoMaxHeight(div, max_height)
    player.one('canplay', () => enforceVideoMaxHeight(div, max_height))
    $(window).on(`resize.${id}`, (e) => enforceVideoMaxHeight(div, max_height))
  }

  $($elem)[0].addEventListener('simplero:media:resize', (e) => {
    sizeWrapper($elem[0])
    if (max_height) {
      enforceVideoMaxHeight(div, max_height)
    }
  })

  enhanceBackgroundPlaybackChancesOnMobileApp(player)
  replaceWithSnapshotBeforeTurboCache($wrapper, snapshot, player)
}

export const initializeAudioPlayer = function (elem) {
  const $elem = $(elem)

  // Bail on setup if the audio is already initialzied.
  if ($elem.closest('.video-js--initialized').length) {
    return
  }

  const snapshot = $elem[0].outerHTML
  const stats_url = $elem.data('stats-url')
  const stats_data = $elem.data('stats-data')

  // Try to help single-audio/video pages start audio playback without too much
  // latency. Not used for multi-audio because it could use too much bandwidth.
  $elem.attr('preload', $('video, audio').length > 1 ? 'metadata' : '')
  $elem.addClass('video-js video-js--initialized')

  ensureUniquePlayerId($elem)

  // Wrap the elem in a sizing wrapper to make the audios resize responsively.
  const $wrapper = $('<div class="audio-wrapper"></div>')
  const width = elem.style.width || $elem.attr('width') || elem.style.maxWidth

  if (width) {
    $wrapper.width(width)
  }

  $elem.wrap($wrapper)

  var data = {
    src: $elem.data('src'),
    type: $elem.data('content-type'),
  }

  if ($elem.data('peaks-file-url')) {
    data['peaks'] = $elem.data('peaks-file-url')
  }

  const options = $elem.data('audio-options')

  if (useModernStyle() && options?.controlBar?.children) {
    options.controlBar.children = Object.fromEntries(
      options.controlBar?.children?.map((item) => {
        if (item == 'VolumePanel') {
          return [item, { inline: false }]
        } else return [item, {}]
      })
    )
  }

  const player = videojs($elem.attr('id'), options, function () {
    // wavesurfer required specific loading
    player.src(data)
  })

  handlePlaybackPositionRestore(player, elem, getPlaybackPosition($elem))
  handlePlaybackRateChange(player, elem)
  applyModernStyleToPlayerIfApplicable($elem, player, options)
  attachSimpleroMediaEvents($elem, player)

  setupPlayTracking(player, stats_url, stats_data)

  player.ready(() => {
    const element = document.getElementById(elem.id)
    const $element = $(element)

    player.one('canplay', () => {
      setupAutoPlay(player, elem)
    })

    $("<div class='flex-break'></div>").insertBefore(
      $element.parent().find('.vjs-current-time')
    )
  })

  enhanceBackgroundPlaybackChancesOnMobileApp(player)
  replaceWithSnapshotBeforeTurboCache($wrapper, snapshot, player)
}

let playerIdCounter = 0
const ensureUniquePlayerId = ($elem) => {
  // Sometimes the same asset can be memoized and rendered multiple times on the same page. Which means we show players with duplicate ids.
  // videojs only initializaed thing by player id
  // So we need to add some random suffix to ensure it's always unique
  $elem.attr('id', $elem.attr('id') + '_uniq_' + playerIdCounter++)
}

$(document).on('page:before-unload', () => {
  Object.values(videojs.getPlayers()).forEach((player) => {
    player?.dispose()
  })
})

function attachSimpleroMediaEvents($elem, player) {
  player.ready(() => {
    const element = document.getElementById($elem.attr('id')) || player.el()
    element.dispatchEvent(
      new CustomEvent('simplero:media:ready', { bubbles: true })
    )
  })
  player.on('play', () => {
    const element = document.getElementById($elem.attr('id')) || player.el()
    const assetId = getAssetId(element)
    element.dispatchEvent(
      new CustomEvent('simplero:media:play', {
        detail: { assetId },
        bubbles: true,
      })
    )
  })
  player.on('ended', () => {
    const element = document.getElementById($elem.attr('id')) || player.el()
    element.dispatchEvent(
      new CustomEvent('simplero:media:ended', { bubbles: true })
    )
  })
}

function replaceWithSnapshotBeforeTurboCache($wrapper, snapshot, player) {
  document.addEventListener(
    'turbo:before-cache',
    () => {
      player.dispose()
      const $target = $wrapper.length == 0 ? $(player.el()) : $wrapper
      $target.replaceWith(snapshot)
    },
    { once: true }
  )
}

window.videojs = videojs
